import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowVersion } from 'dashboard/models/workflow-version';

export interface InquiryPreviewState {
  inquiry: Inquiry | undefined;
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  inquiryTemplate: InquiryTemplate | undefined;
  inquiryTemplateVersionSchema: InquiryTemplateVersionSchema | undefined;
  previewScreenDimensions: ScreenDimensions | undefined;
  accountTypesFilterOptions: AccountTypesFilterOption[] | undefined;
}

export interface InquiryTemplateVersionSchema {
  workflowVersions: Record<string, WorkflowVersion>;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  matchRequirementProfiles: Record<string, MatchRequirementProfile[]>;
}

export const initialState: InquiryPreviewState = {
  inquiry: undefined,
  inquiryTemplateVersion: undefined,
  inquiryTemplate: undefined,
  inquiryTemplateVersionSchema: undefined,
  previewScreenDimensions: undefined,
  accountTypesFilterOptions: undefined,
};

interface SetInquiryPayload {
  inquiry: Inquiry;
}

interface SetInquiryTemplateVersionPayload {
  inquiryTemplateVersion: InquiryTemplateVersion;
}

interface SetInquiryTemplatePayload {
  inquiryTemplate: InquiryTemplate;
}

interface SetInquiryTemplateVersionSchemaPayload {
  inquiryTemplateVersionSchema: InquiryTemplateVersionSchema;
}

interface SetPreviewScreenDimensionsPayload {
  screenDimensions: ScreenDimensions;
}

interface SetAccountTypesFilterOptionsPayload {
  accountTypesFilterOptions: AccountTypesFilterOption[];
}

const { reducer, actions } = createSlice({
  name: 'inquiry-preview',
  initialState,
  reducers: {
    setInquiry: (state, action: PayloadAction<SetInquiryPayload>) => {
      state.inquiry = castDraft(action.payload.inquiry);
    },
    setInquiryTemplateVersion: (state, action: PayloadAction<SetInquiryTemplateVersionPayload>) => {
      state.inquiryTemplateVersion = action.payload.inquiryTemplateVersion;
    },
    setInquiryTemplate: (state, action: PayloadAction<SetInquiryTemplatePayload>) => {
      state.inquiryTemplate = action.payload.inquiryTemplate;
    },
    setInquiryTemplateVersionSchema: (
      state,
      action: PayloadAction<SetInquiryTemplateVersionSchemaPayload>,
    ) => {
      state.inquiryTemplateVersionSchema = action.payload.inquiryTemplateVersionSchema;
    },
    setPreviewScreenDimensions: (
      state,
      action: PayloadAction<SetPreviewScreenDimensionsPayload>,
    ) => {
      state.previewScreenDimensions = action.payload.screenDimensions;
    },
    setAccountTypesFilterOptions: (
      state,
      action: PayloadAction<SetAccountTypesFilterOptionsPayload>,
    ) => {
      state.accountTypesFilterOptions = action.payload.accountTypesFilterOptions;
    },
  },
});

export const {
  setInquiry,
  setInquiryTemplateVersion,
  setInquiryTemplate,
  setInquiryTemplateVersionSchema,
  setPreviewScreenDimensions,
  setAccountTypesFilterOptions,
} = actions;

export const inquiryPreviewReducer = reducer;
